import { useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { zodValidator } from '@tanstack/zod-adapter';
import { z } from 'zod';
import { Box, Flex, Text } from '@chakra-ui/react';
import { Button } from '@/components/ui/Button';
import { toaster } from '@/components/ui/Toaster';
import { CompanyTable } from '@/features/companies/components/CompanyTable';
import { CompanyForm } from '@/features/companies/components/CompanyForm';
import { companyQueries, useCreateCompany } from '@/features/companies/api/companyQueries';

const companiesSearchSchema = z.object({
  page: z.number().int().positive().catch(1),
  pageSize: z.number().int().min(1).max(100).catch(20),
  sortBy: z.enum(['name', 'createdAt', 'updatedAt']).optional().catch(undefined),
  sortOrder: z.enum(['asc', 'desc']).optional().catch(undefined),
  name: z.string().max(255).optional().catch(undefined),
});

export const Route = createFileRoute('/_authenticated/companies/')({
  validateSearch: zodValidator(companiesSearchSchema),
  loaderDeps: ({ search }) => ({ search }),
  loader: ({ context, deps }) =>
    context.queryClient.ensureQueryData(companyQueries.list(deps.search)),
  component: CompaniesPage,
});

function CompaniesPage() {
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const [showCreate, setShowCreate] = useState(false);
  const createMutation = useCreateCompany();

  return (
    <Box>
      <Flex justify="space-between" align="center" mb={6}>
        <Text fontSize="2xl" fontWeight="bold">
          企業マスタ
        </Text>
        <Button colorPalette="brand" onClick={() => setShowCreate((v) => !v)}>
          {showCreate ? 'キャンセル' : '新規作成'}
        </Button>
      </Flex>

      {showCreate && (
        <Box bg="bg.panel" p={4} borderRadius="md" shadow="sm" mb={6}>
          <Text fontWeight="medium" mb={3}>
            新規企業
          </Text>
          <CompanyForm
            onSubmit={async (values) => {
              try {
                await createMutation.mutateAsync(values);
                toaster.success({ title: '企業を作成しました' });
                setShowCreate(false);
              } catch {
                toaster.error({ title: '保存に失敗しました。時間をおいて再度お試しください' });
              }
            }}
            submitLabel="作成"
          />
        </Box>
      )}

      <CompanyTable
        search={search}
        onSearchChange={(partial) => navigate({ search: (prev) => ({ ...prev, ...partial }) })}
        onRowClick={(id) =>
          navigate({ to: '/companies/$companyId', params: { companyId: String(id) } })
        }
      />
    </Box>
  );
}
